import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { Currency } from 'api/account';
import { User } from 'api/models';
import { ReactComponent as StarRating } from 'assets/svgs/rating-star.svg';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { EmptyCell } from 'components/atoms/empty-cell';
import { humanise } from 'components/atoms/humanised-date';
import { UserTag } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import { Color } from 'components/colors';
import { NoValue } from 'components/organisms/tasks/field-value';
import { ProductiveHeading07 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { activeUsersAndTeamsFromIndexedB } from 'queries/members';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { TextWrapper, MultiSelectWrapper, DateWrapper } from './style';

const Value = styled.span`
  ${ProductiveHeading07};
`;

export const RatingRenderer = ({ value, scale }): JSX.Element => {
  return value && scale ? (
    <div>
      <StarRating /> <Value>{value}</Value> / {scale}
    </div>
  ) : (
    <EmptyCell />
  );
};
export const RenderText = ({ value }) => {
  if (value) {
    return <TextWrapper>{value}</TextWrapper>;
  }

  return <NoValue />;
};
export const RenderBoolean = ({ value }: { value: boolean }) => {
  const translateTrue = useTranslation('yes');
  const translateFalse = useTranslation('no');

  if (value) {
    return <RenderText value={translateTrue} />;
  }

  return <RenderText value={translateFalse} />;
};

export const SingleUser = ({
  userId,
  user,
  isPublished,
}: {
  userId: number;
  user?: User;
  isPublished?: boolean;
}) => {
  const activeUsersAndTeams =
    activeUsersAndTeamsFromIndexedB.getActiveUsersAndTeams;
  const { users } = activeUsersAndTeams;
  const selected = isPublished
    ? user
    : users?.find?.((x) => x.userId === userId);

  if (selected == null) {
    return <NoValue />;
  }
  const imageUrl = selected.photoUrl;
  const label = fullName(selected);

  return (
    <MultiSelectWrapper>
      <UserTag
        color={getRandomColor(selected.userId)}
        imageUrl={imageUrl}
        label={label}
        userStatus={selected?.userStatus}
      />
    </MultiSelectWrapper>
  );
};
export const MultiUser = ({
  userIds,
  users,
  isPublished,
}: {
  userIds?: number[] | undefined;
  users?: User[];
  isPublished?: boolean;
}) => {
  if ((!userIds || userIds.length === 0) && users && users?.length === 0) {
    return <NoValue />;
  }

  return (
    <MultiSelectWrapper>
      {isPublished
        ? (users || [])
            ?.slice(0, 5)
            ?.map((user) => (
              <SingleUser
                userId={user?.userId}
                key={user?.userId}
                isPublished
                user={user}
              />
            ))
        : userIds &&
          (userIds || [])
            .slice(0, 5)
            ?.map((userId) => <SingleUser userId={userId} key={userId} />)}

      {isPublished ? (
        users && users?.length > 5
      ) : userIds && userIds.length > 5 ? (
        <Tag color={'blue' || getRandomColor('blue')}>{`+ ${
          isPublished
            ? users && users?.length - 5
            : userIds && userIds.length - 5
        }`}</Tag>
      ) : null}
    </MultiSelectWrapper>
  );
};
export const SingleSelect = ({
  color,
  label,
}: {
  color: Color | '';
  label?: string;
}) => {
  if (label) {
    return <Tag color={color || getRandomColor(label)}>{label}</Tag>;
  }

  return <NoValue />;
};
export const MultiSelect = ({ options }) => {
  if (options) {
    return (
      <MultiSelectWrapper>
        {options?.map((option) => (
          <SingleSelect
            color={option.color}
            label={option.label}
            key={option.label}
          />
        ))}
      </MultiSelectWrapper>
    );
  }

  return <NoValue />;
};

export const TimeRenderer = ({ time }: { time: string | number }) => {
  return <DateWrapper>{humanise(moment(time))}</DateWrapper>;
};
export const NumberofDays = ({ value }: { value?: number }) => {
  if (!value) {
    return <NoValue />;
  }

  return <div>{`${value}d`}</div>;
};

export const RenderCurrency = ({
  value,
  currency,
}: {
  value?: number;
  currency?: Currency;
}) => {
  if (!value) {
    return <NoValue />;
  }

  return <div>{currencyRenderer(value, currency)}</div>;
};
